import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL =
  'https://circumvent.io/blog/the-feature-trap-security-industry-outcomes';
const BLOG_POST_PUBLISHED_AT = '2026-07-13';
/** Body ~850 words. Read time: max(1, round(words / 200 wpm)). */
const BLOG_POST_READ_TIME = '4 min read';
const BLOG_POST_AUTHOR = 'Erin Howell';
const FEATURE_IMAGE_SRC = '/images/blog/feature-trap-battlecard-vs-outcomes-v4.jpg';
const BLOG_POST_OG_TITLE =
  "The Feature Trap that's ensnared the Security industry and stopped it focusing on Outcomes | Circumvent";
const BLOG_POST_OG_DESCRIPTION =
  'Security tools competed on control counts and feature parity. That output race created noise. Why Circumvent is rebuilding around outcomes instead.';
const BLOG_POST_OG_IMAGE_URL =
  'https://circumvent.io/images/blog/feature-trap-battlecard-vs-outcomes-v4.jpg';

const BlogFeatureTrapPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              The Feature Trap that&apos;s ensnared the Security industry and stopped it focusing on
              Outcomes
            </h1>
            <p className="blog-post__lead">
              Security tools competed on control counts and feature parity. That output race created
              noise. Why Circumvent is rebuilding around outcomes instead.
            </p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Perspective</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>13 Jul 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt="Comparison of feature-parity battle cards against an outcomes-focused approach to security posture."
                  width={1376}
                  height={768}
                  loading="eager"
                  decoding="async"
                />
              </figure>
              <p>
                Circumvent is my second time working on a cloud security product. When I joined the
                Cloud Conformity team in 2019 the CSPM category didn&apos;t have a name yet, and
                only a couple of competitors, each of which were still independent startups. We
                focused on building the detection rules for cloud configuration that our founding
                team wished they had available during a cloud migration project for a government
                department. During that project they had identified a gap in the market and it was
                our mission to fill that gap. Our mission was to be the leader at finding
                misconfigurations.
              </p>
              <p>
                Product management practice was still maturing during that time as well, and we were
                more focused on features and keeping up with new cloud services than concepts like
                jobs-to-be-done or outcome-driven-development. As the competitor landscape filled up
                and industry analysts started to define the CSPM category we started facing more
                product evaluations comparing Conformity functionality against other similar
                products. These took the form of evaluation frameworks, battle cards, magic
                quadrants and waves, and mostly consisted of feature breakdowns, how many public
                clouds were supported, and rule counts for different cloud services.
              </p>
              <p>
                This created a market pressure to compete on feature parity and the amount of
                misconfigurations we could detect. This mindset shaped not only our product but all
                products in the security industry. I believe it&apos;s a big part of the reason
                security lags behind the rest of the SaaS software development industry in shifting
                towards impact-first and outcome driven development. Using OKR language of
                &apos;measure what matters&apos;, we were measuring and optimizing for the output
                (rules count), instead of outcome (risk reduction).
              </p>
              <p>
                As my own career and the PM industry evolved and adopted outcomes as the goal
                instead of outputs, I started to realize the product we were so proud of had solved
                one problem very well, but created other problems that our customers were not
                equipped to deal with.
              </p>

              <h2 id="the-warning-signs">The warning signs</h2>
              <p>
                As the products competed against each other, successfully pushing the issues
                detected numbers higher and higher, we started to hear customers complain they were
                drowning in findings. We saw organizations who were committed to their security
                work, engaging with devops, implementing shift-left and still barely moving the
                needle. All the effort they channeled into their security posture was spent
                treading water as they tried to keep up with the rate of change and rates of new
                detections.
              </p>
              <p>
                We were giving them more visibility, sure. But we were also giving them more alerts,
                more false positives, and more tickets that their engineering teams didn&apos;t have
                the time or context to fix. We were unintentionally contributing to the very noise
                that made security teams feel like they were failing.
              </p>
              <p>
                When you inundate a product engineer with thousands of &quot;critical&quot; alerts,
                you aren&apos;t helping them secure their environment. You&apos;re teaching them to
                tune out. Every alert becomes background noise, and true risk gets buried under a
                mountain of tickets that have no business being labeled &quot;urgent.&quot;
              </p>

              <h2 id="the-problems">The problems</h2>
              <p>
                Rules based controls are what the industry has relied on for decades, they rely
                on binary logic, usually with a limited number of attributes considered. This
                results in a lot of false positives. False positive is a catch-phrase used to
                categorize any finding where the risk reported does not actually represent real
                risk. This usually isn&apos;t because the rule logic is wrong, it&apos;s because
                the rule logic looks at too few attributes, or is limited to data from one tool, so
                a risk is reported but it&apos;s actually mitigated by controls elsewhere in the
                architecture.
              </p>
              <p>
                Risks are categorized based on worst case scenario. Criticality assumes the
                application is highly important to the business, and handles highly sensitive data.
                It assumes the likelihood of an exploitation occurring is high, and the impact of an
                exploitation occurrence is severe. In a significant number of cases one or more of
                these assumptions is incorrect, resulting in risks being over prioritized.
              </p>
              <p>
                Vendors and industry researchers categorize risk based on standardized frameworks
                and policy templates, but customers have different risk appetites. Even within an
                industry vertical like banking, which is subject to regulation, we see variation in
                risk appetite. We see this not only globally but even within
                organizations that work in the same country. Most organizations will start with a
                standard risk policy but tailor it after debate within the organization, this can
                determine how criticality levels are defined, how data is classified, and how
                SLAs, risk acceptance and suppression are handled.
              </p>
              <p>
                For a long time the industry has leaned into automated remediation as the fix for
                the noise. When issues can be detected at machine speed the answer must be to
                remediate at machine speed. Unfortunately that hasn&apos;t worked, we&apos;ve seen
                automated pull requests get opened at scale, shifting the bottleneck from security
                to engineering. We&apos;ve also seen engineers get frustrated with these pull
                requests (PRs) that apply a fix without contextual awareness of architecture or
                established practices, or which patch the issue but break functionality. Because of
                this, PRs that are opened from automations are often ignored or closed, and PRs that
                don&apos;t get shipped don&apos;t fix risks.
              </p>

              <h2 id="the-shift-to-outcomes">The shift to outcomes</h2>
              <p>
                Joining Circumvent has been my opportunity for a do-over that most Product Managers
                never get.
                This time it&apos;s about acknowledging the problems the industry faces, which we
                ourselves contributed to while building Conformity. This time our goal has been
                &apos;less is more&apos;, to help our customers find what actually matters, and fix
                it.
              </p>
              <p>An outcome-focused approach to security posture includes:</p>
              <ul>
                <li>
                  <strong>Understanding:</strong> Customer architecture, their risk appetite, their
                  org structure and processes.
                </li>
                <li>
                  <strong>Deciding:</strong> Investigating, validating, root cause analysis,
                  prioritization and recommendation.
                </li>
                <li>
                  <strong>Acting:</strong> Remediation plans with the smartest and most context
                  aware fix possible, workflows that leverage the tools and processes teams use to
                  actually get work into a sprint and completed.
                </li>
              </ul>
              <p>
                We are moving away from the era of &quot;find everything&quot; and into an era of
                &quot;intelligent prioritization.&quot; It&apos;s the only way we&apos;re going to
                keep up with the speed of machine detection, and bridge the cultural gap between
                security and development teams.
              </p>
              <p>
                We don&apos;t need more scanners. We need more clarity. It&apos;s time to stop
                chasing the noise and start delivering results that actually make an environment
                more secure. This time we&apos;re holding ourselves and the product accountable to
                customer outcomes; every product decision and every design decision needs to
                contribute to customers reducing their security risk.
              </p>
              <p>
                Reach out if you&apos;d like to talk through these problems, or work with us as a
                design partner while we build a product focused on outcomes.
              </p>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-warning-signs">
                      The warning signs
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-problems">
                      The problems
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-shift-to-outcomes">
                      The shift to outcomes
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogFeatureTrapPage;
